// 화면 19 — 종목 검색 autocomplete (P0).
// 권위: ARCH_DASHBOARD §4.19 / §6.2 — GET /api/v1/stocks/search?q=&limit=20.
// 300ms debounce, name/symbol prefix 매칭은 백엔드 책임.
// 선택 시 부모 콜백 호출 → URL ?symbol= 동기화는 부모 페이지가 담당.

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useStockSearch } from './useStockScoreExplorer';
import type { StockMatch } from './types';

interface Props {
  selectedSymbol: string | null;
  onSelect: (symbol: string) => void;
}

const DEBOUNCE_MS = 300;

export const StockSearchAutocomplete = memo(function StockSearchAutocomplete({
  selectedSymbol,
  onSelect,
}: Props): JSX.Element {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // debounce.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  const { data, isFetching, isError, error } = useStockSearch(debounced);

  const matches: StockMatch[] = useMemo(() => data?.matches ?? [], [data]);

  // 외부 클릭 시 드롭다운 닫기.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDocClick);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, []);

  // 매칭 변화 시 active 인덱스 0 으로.
  useEffect(() => {
    setActiveIdx(0);
  }, [debounced, matches.length]);

  const handleSelect = (m: StockMatch) => {
    onSelect(m.symbol);
    setInput(''); // 선택 후 입력 비움 (URL ?symbol= 표시로 충분).
    setOpen(false);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const m = matches[activeIdx];
      if (m) handleSelect(m);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="sse-search" ref={wrapRef}>
      <label htmlFor="sse-search-input" className="sse-search__label">
        종목 검색
      </label>
      <input
        id="sse-search-input"
        type="text"
        className="sse-search__input"
        placeholder="종목명 / 종목코드 (예: 삼성전자, 005930)"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKey}
        autoComplete="off"
        data-testid="sse-search-input"
      />
      {selectedSymbol && !input && (
        <span className="sse-search__current">
          선택: <code>{selectedSymbol}</code>
        </span>
      )}
      {open && debounced.length >= 1 && (
        <div className="sse-search__dropdown" role="listbox">
          {isFetching && <div className="sse-search__hint">검색 중…</div>}
          {isError && (
            <div className="sse-search__hint sse-search__hint--error">
              검색 실패: {error?.message ?? 'unknown'}
            </div>
          )}
          {!isFetching && !isError && matches.length === 0 && (
            <div className="sse-search__hint">매칭 결과 없음.</div>
          )}
          {matches.map((m, i) => (
            <button
              key={m.symbol}
              type="button"
              role="option"
              aria-selected={i === activeIdx}
              className={
                'sse-search__option' +
                (i === activeIdx ? ' sse-search__option--active' : '')
              }
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => handleSelect(m)}
            >
              <span className="sse-search__opt-name">{m.name}</span>
              <span className="sse-search__opt-symbol">{m.symbol}</span>
              <span className="sse-search__opt-market">{m.market_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});
